export interface InstructionalModel {
  id: string;
  name: string;
  category: string;
  shortDescription: string;
  metadata: {
    bestFor: string;
    corePhilosophy: string;
    typicalStructure: string[];
  };
}

export const INSTRUCTIONAL_MODELS: InstructionalModel[] = [
  {
    id: 'direct-instruction',
    name: 'Direct Instruction',
    category: 'Teacher-Centered',
    shortDescription: 'Explicit teaching with guided and independent practice.',
    metadata: {
      bestFor:
        'Foundational skills, clear facts, step-by-step procedures (e.g., math equations, grammar rules).',
      corePhilosophy: 'Explicit teaching followed by practice.',
      typicalStructure: [
        'I Do (Teacher demonstration)',
        'We Do (Guided practice)',
        'You Do (Independent practice)',
      ],
    },
  },
  {
    id: '5e-model',
    name: '5E Model',
    category: 'Constructivist / Science',
    shortDescription: 'Experience-first approach through observation and exploration.',
    metadata: {
      bestFor: 'Science and STEM lessons; building understanding through observation.',
      corePhilosophy: 'Experience first, vocabulary second.',
      typicalStructure: [
        'Engage (Hook)',
        'Explore (Hands-on investigation)',
        'Explain (Concept definition)',
        'Elaborate (Application)',
        'Evaluate (Assessment)',
      ],
    },
  },
  {
    id: 'inquiry-based',
    name: 'Inquiry-Based Learning',
    category: 'Student-Led / Discovery',
    shortDescription: 'Learning driven by student questions and investigations.',
    metadata: {
      bestFor: 'Complex interdisciplinary projects, social studies, advanced science.',
      corePhilosophy: 'Learning starts with a question, not a fact. Teacher acts as facilitator.',
      typicalStructure: [
        'Pose a Big Question',
        'Student Research/Investigation',
        'Presentation of Findings',
      ],
    },
  },
  {
    id: 'workshop-model',
    name: 'Workshop Model',
    category: 'Skills-Based / ELA',
    shortDescription: 'Mini-lessons followed by independent practice and conferring.',
    metadata: {
      bestFor: 'English Language Arts (Reading/Writing), fostering independence.',
      corePhilosophy: 'Students act as practitioners (writers/readers); teacher coaches.',
      typicalStructure: [
        'Mini-Lesson (10-15 mins)',
        'Work Time & Conferring (30-40 mins)',
        'Share (5-10 mins)',
      ],
    },
  },
  {
    id: 'pbl',
    name: 'Project-Based Learning (PBL)',
    category: 'Product-Oriented',
    shortDescription: 'Real-world projects as the core learning experience.',
    metadata: {
      bestFor: 'Real-world application, long-term units.',
      corePhilosophy:
        'The project is the unit. Learning happens while creating a solution for a real audience.',
      typicalStructure: ['Launch (Entry Event)', 'Sustained Inquiry', 'Public Product/Presentation'],
    },
  },
  {
    id: 'flipped-classroom',
    name: 'Flipped Classroom',
    category: 'Inverted Delivery',
    shortDescription: 'Content at home, practice and support in class.',
    metadata: {
      bestFor: 'Maximizing class time for practice rather than lecturing.',
      corePhilosophy: "Content consumption happens at home; 'homework' happens in class.",
      typicalStructure: [
        'At Home: Watch video lecture/Read content',
        'In Class: Problem sets, labs, support',
      ],
    },
  },
  {
    id: 'cooperative-learning',
    name: 'Cooperative Learning (Jigsaw)',
    category: 'Social / Peer-Teaching',
    shortDescription: 'Students learn parts and teach each other.',
    metadata: {
      bestFor: 'Large content loads, enforcing participation.',
      corePhilosophy: 'Students are interdependent; they must teach each other to succeed.',
      typicalStructure: [
        'Divide topic into chunks',
        'Expert Groups (Learn the chunk)',
        'Home Groups (Teach the chunk to peers)',
      ],
    },
  },
  {
    id: 'problem-based',
    name: 'Problem-Based Learning (PrBL)',
    category: 'Case-Study Based',
    shortDescription: 'Solve real-world case studies to learn concepts.',
    metadata: {
      bestFor: 'Medical scenarios, law, civics, applied math.',
      corePhilosophy: 'Structured around solving a specific, ill-defined case study.',
      typicalStructure: [
        'Present the Case',
        'Identify Knowledge Gaps',
        'Research & Apply Solutions',
      ],
    },
  },
  {
    id: 'udl',
    name: 'Universal Design for Learning (UDL)',
    category: 'Inclusive Framework',
    shortDescription: 'Multiple means of engagement, representation, and expression.',
    metadata: {
      bestFor: 'Diverse classrooms with varying needs and learning styles.',
      corePhilosophy:
        'Provide multiple means of Engagement, Representation, and Action/Expression.',
      typicalStructure: [
        'Variable Input (Video/Text/Audio)',
        'Student Choice in Output (Write/Build/Record)',
      ],
    },
  },
];
